import 'react-app-polyfill/ie11';
import 'core-js';
import React from 'react';
import ReactDOM from 'react-dom';
import './index.scss';
import './i18n/i18n';
import App from './App';
import { Provider } from 'react-redux';
import { store, persistor, history } from './redux/store';
import { PersistGate } from 'redux-persist/integration/react';
import * as serviceWorker from './serviceWorker';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { initUserpilot } from './services/userpilot';
import { startAppUpdateMonitor } from './services/appUpdates';
import { tabIndexes } from './models/TabIndexes';

// MSAL imports
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { loginRequest, msalConfig } from './authConfig';
export const msalInstance = new PublicClientApplication(msalConfig);

// Account selection logic is app dependent. Adjust as needed for different use cases.
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
  }
});

// handle auth redired/do all initial setup for msal
msalInstance
  .handleRedirectPromise()
  .then(authResult => {
    // Check if user signed in
    const account = msalInstance.getActiveAccount();
    if (!account) {
      // redirect anonymous user to login page
      msalInstance.loginRedirect(loginRequest);
    }
  })
  .catch(err => {
    // TODO: Handle errors
    console.error(err);
  });

const customTheme = createMuiTheme({
  typography: {
    fontFamily: 'Source Sans Pro',
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
});

// Userpilot initialization
initUserpilot()

// React uses production mode for every optimized build, including QA and STG.
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_BUILD_ID) {
  // The reload URL bypasses cached HTML; remove its marker before mounting the router.
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.has('_app_build')) {
    currentUrl.searchParams.delete('_app_build');
    history.replace(currentUrl.pathname + currentUrl.search + currentUrl.hash);
  }

  startAppUpdateMonitor({
    buildId: process.env.REACT_APP_BUILD_ID,
    getNavigation: () => {
      const { tabValue } = store.getState();
      const url = new URL(window.location.href);
      const home = url.pathname.toLowerCase() === '/home';
      const projectDetail = url.pathname.toLowerCase() === '/projectdetail';
      const safeHomeTab = [tabIndexes.portfolio, tabIndexes.projects, tabIndexes.earlyWarnings, tabIndexes.reviews]
        .indexOf(tabValue) !== -1;
      if (home) url.searchParams.set('tabSelected', tabIndexes[tabValue]);
      return {
        key: `${url.pathname}:${projectDetail ? url.searchParams.get('id') : ''}:${home ? tabValue : ''}`,
        safe: (home && safeHomeTab) || projectDetail,
        url: url.href,
      };
    },
    subscribe: listener => {
      const unsubscribeStore = store.subscribe(listener);
      const unsubscribeHistory = history.listen(listener);
      return () => { unsubscribeStore(); unsubscribeHistory(); };
    },
  });
}

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <MuiThemeProvider theme={customTheme}>
        <App pca={msalInstance} />
      </MuiThemeProvider>
    </PersistGate>
  </Provider>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
